import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import AdminLogin from './AdminLogin';

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
`;

const Title = styled.h1`
  color: #333;
  margin: 0;
  font-size: 2rem;
  font-weight: 600;
`;

const WelcomeText = styled.p`
  color: #666;
  margin: 5px 0 0 0;
  font-size: 14px;
`;

const StatsCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  border-radius: 12px;
  text-align: center;
  min-width: 150px;
`;

const StatsNumber = styled.div`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 5px;
`;

const StatsLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.9;
`;

const SearchContainer = styled.div`
  margin-bottom: 25px;
  display: flex;
  gap: 15px;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  width: 300px;
  outline: none;
  
  &:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const RefreshButton = styled.button`
  background: #667eea;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #a0aec0;
    cursor: not-allowed;
    transform: none;
  }
`;

const LogoutButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s;
  
  &:hover {
    background: #c82333;
  }
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f8fafc;
`;

const TableRow = styled.tr`
  &:hover {
    background: #f8fafc;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #e2e8f0;
  }
`;

const TableCell = styled.td`
  padding: 16px 20px;
  text-align: left;
  vertical-align: middle;
`;

const TableHeaderCell = styled.th`
  padding: 18px 20px;
  text-align: left;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fed7d7;
  color: #c53030;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  border-left: 4px solid #e53e3e;
`;

const SubscriptionSelect = styled.select`
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.85rem;
  color: #4a5568;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const StatusBadge = styled.span`
  background: ${props => {
    switch(props.status) {
      case 'adminPremium': return '#c6f6d5';
      case 'monthly': return '#bee3f8';
      case 'annually': return '#d6f5d6';
      default: return '#fed7d7';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'adminPremium': return '#22543d';
      case 'monthly': return '#2c5282';
      case 'annually': return '#1a202c';
      default: return '#c53030';
    }
  }};
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
`;

// Firebase Functions Configuration
const FIREBASE_FUNCTIONS_BASE_URL = 'https://us-central1-stronger-and-free.cloudfunctions.net';

// Main Component
const BeestmodeAdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingUsers, setUpdatingUsers] = useState(new Set());
  const [stats, setStats] = useState({ totalUsers: 0, premiumUsers: 0 });
  
  const { user, logout, getIdToken } = useAuth();

  // Helper function to get auth header
  const getAuthHeader = async () => {
    const token = await getIdToken();
    return token ? `Bearer ${token}` : null;
  };

  // API functions with authentication
  const fetchUsers = async () => {
    try {
      const authHeader = await getAuthHeader();
      
      if (!authHeader) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${FIREBASE_FUNCTIONS_BASE_URL}/getAllUsers`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        }
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Unauthorized access. Admin privileges required.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch users');
      }

      return data.users;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  };

  const updateSubscriptionStatus = async (uid, status) => {
    try {
      const authHeader = await getAuthHeader();
      
      if (!authHeader) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${FIREBASE_FUNCTIONS_BASE_URL}/updateSubscriptionStatus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({ uid, status })
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Unauthorized access. Admin privileges required.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to update subscription status');
      }

      return data;
    } catch (error) {
      console.error('Error updating subscription status:', error);
      throw error;
    }
  };

  const fetchUserStats = async () => {
    try {
      const authHeader = await getAuthHeader();
      
      if (!authHeader) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${FIREBASE_FUNCTIONS_BASE_URL}/getUserStats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        }
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Unauthorized access. Admin privileges required.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch stats');
      }

      return data.stats;
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (user) {
      loadUsers();
      loadStats();
    }
  }, [user]);

  const loadStats = async () => {
    try {
      const statsData = await fetchUserStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error loading stats:', err);
      if (err.message.includes('Unauthorized')) {
        setError('Access denied. You do not have admin privileges.');
      }
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const userData = await fetchUsers();
      setUsers(userData);
      
      // Update local stats after loading users
      const totalUsers = userData.length;
      const premiumUsers = userData.filter(user => user.isPremium).length;
      setStats(prev => ({ ...prev, totalUsers, premiumUsers }));
      
    } catch (err) {
      if (err.message.includes('Unauthorized')) {
        setError('Access denied. You do not have admin privileges.');
      } else {
        setError('Failed to load users. Please check your connection and try again.');
      }
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionChange = async (uid, newStatus) => {
    try {
      setUpdatingUsers(prev => new Set(prev).add(uid));
      
      await updateSubscriptionStatus(uid, newStatus);
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.uid === uid 
            ? { 
                ...user, 
                subscriptionStatus: newStatus,
                isPremium: ['adminPremium', 'monthly', 'annually'].includes(newStatus)
              }
            : user
        )
      );
    } catch (err) {
      if (err.message.includes('Unauthorized')) {
        setError('Access denied. You do not have admin privileges.');
      } else {
        setError('Failed to update subscription status. Please try again.');
      }
      console.error('Error updating subscription status:', err);
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(uid);
        return newSet;
      });
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalUsers = stats.totalUsers || users.length;
  const premiumUsers = stats.premiumUsers || users.filter(user => user.isPremium).length;

  if (loading) {
    return (
      <Container>
        <LoadingSpinner />
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div>
          <Title>Firebase Admin Dashboard</Title>
          <WelcomeText>Welcome, {user?.email}</WelcomeText>
        </div>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
          <StatsCard>
            <StatsNumber>{totalUsers}</StatsNumber>
            <StatsLabel>Total Users</StatsLabel>
          </StatsCard>
          <StatsCard>
            <StatsNumber>{premiumUsers}</StatsNumber>
            <StatsLabel>Premium Users</StatsLabel>
          </StatsCard>
          <LogoutButton onClick={handleLogout}>
            Sign Out
          </LogoutButton>
        </div>
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Search by email or name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <RefreshButton onClick={loadUsers} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </RefreshButton>
      </SearchContainer>

      <Table>
        <TableHeader>
          <tr>
            <TableHeaderCell>User</TableHeaderCell>
            <TableHeaderCell>Email</TableHeaderCell>
            <TableHeaderCell>Templates</TableHeaderCell>
            <TableHeaderCell>Workouts</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell>Actions</TableHeaderCell>
          </tr>
        </TableHeader>
        <tbody>
          {filteredUsers.map(user => (
            <TableRow key={user.uid}>
              <TableCell>
                <strong>{user.name}</strong>
              </TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.templatesCount}</TableCell>
              <TableCell>{user.workoutsCount}</TableCell>
              <TableCell>
                <StatusBadge status={user.subscriptionStatus || 'free'}>
                  {user.subscriptionStatus === 'adminPremium' ? 'Admin Premium' :
                  user.subscriptionStatus === 'monthly' ? 'Monthly' :
                  user.subscriptionStatus === 'annually' ? 'Annual' : 'Free'}
                </StatusBadge>
              </TableCell>
              <TableCell>
                <SubscriptionSelect
                  value={user.subscriptionStatus || 'free'}
                  onChange={(e) => handleSubscriptionChange(user.uid, e.target.value)}
                  disabled={updatingUsers.has(user.uid)}
                >
                  <option value="free">Free</option>
                  <option value="adminPremium">Admin Premium</option>
                  <option value="monthly">Monthly</option>
                  <option value="annually">Annual</option>
                </SubscriptionSelect>
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>

      {filteredUsers.length === 0 && searchTerm && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          No users found matching "{searchTerm}"
        </div>
      )}
    </Container>
  );
};

// Main App wrapper with authentication
const AdminDashboardApp = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return user ? <BeestmodeAdminDashboard /> : <AdminLogin />;
};

export default AdminDashboardApp;